import { FC, useEffect, useMemo } from "react";
import {
  alpha,
  Box,
  Stack,
  ToggleButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AccountBalanceOutlined,
  AccountBalanceWalletOutlined,
  CheckCircle,
  CreditCardOutlined,
  LocalAtmOutlined,
  LockOutlined,
  PaymentsOutlined,
  PointOfSaleOutlined,
  ReceiptLongOutlined,
} from "@mui/icons-material";
import { useAtomValue } from "jotai";
import { useFormContext, useWatch } from "react-hook-form";

import { TPaymentMethod, TPaymentProvider } from "@/api";
import { PURCHASES } from "@/config/resources";
import { useAdmin } from "@/hooks/admin";
import { i18nProvider } from "@/providers/i18n";

import { prePurchaseAtom } from "../post/store";

const PAYMENT_ERRORS: Record<string, string> = {
  AMOUNT_TO_PAY_IS_LOWER_THAN_REQUIRED: "El monto a pagar es menor que el requerido",
  AMOUNT_TO_PAY_IS_UPPER_THAN_REQUIRED: "El monto a pagar es mayor al requerido",
  CLIENT_ID_IS_REQUIRED: "El documento de identidad es requerido",
  KALTO_ONBOARDING_IS_REQUIRED: "Onboarding de Kalto requerido",
  DISABLED_BY_DEBT: "Deshabilitado por deuda",
  DISABLED_BY_REJECTIONS: "Deshabilitado por rechazos",
  NOT_ENOUGH_BALANCE: "El usuario no cuenta con el cupo suficiente",
  DISABLED_BY_SUPPLIER: "Deshabilitado por el distribuidor",
};

const ICON_BY_METHOD: Record<string, React.ReactNode> = {
  ONLINE: <CreditCardOutlined />,
  UPON_DELIVERY: <LocalAtmOutlined />,
  UPON_DELIVERY_SEERI: <LocalAtmOutlined />,
  CREDIT: <AccountBalanceWalletOutlined />,
  WIRE_TRANSFER: <AccountBalanceOutlined />,
  CASH: <PaymentsOutlined />,
  POS: <PointOfSaleOutlined />,
  INVOICE: <ReceiptLongOutlined />,
};

const prettify = (code: string) =>
  code
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const methodLabel = (code: string) => {
  const key = `resources.${PURCHASES}.fields.paymentMethods.${code}`;
  const translated = i18nProvider.translate(key);
  return translated === key ? prettify(code) : translated;
};

const providerLabel = (code: string) => {
  const key = `resources.${PURCHASES}.fields.paymentProviders.${code}`;
  const translated = i18nProvider.translate(key);
  return translated === key ? prettify(code) : translated;
};

export const PaymentStep: FC = () => {
  const { setValue } = useFormContext();
  const { role, hasPermission } = useAdmin();
  const prePurchase = useAtomValue(prePurchaseAtom);
  const paymentMethod = useWatch({ name: "paymentMethod" }) as string | null;
  const paymentProvider = useWatch({ name: "paymentProvider" }) as string | null;

  const { available, unavailable, providers } = useMemo(() => {
    let available: TPaymentMethod[] = [];
    let unavailable: TPaymentMethod[] = [];
    let providers: TPaymentProvider[] = [];

    if (prePurchase) {
      available = prePurchase.paymentMethods.filter(
        (p) => p.isValid && p.code !== "PARTIAL_PAYMENT"
      );
      unavailable = prePurchase.paymentMethods.filter(
        (p) => !p.isValid && p.code !== "PARTIAL_PAYMENT"
      );

      available = available.filter(
        (p) =>
          p.code !== "UPON_DELIVERY_SEERI" ||
          hasPermission("purchase.create.uponDeliverySeeri") ||
          role.name === "Admin"
      );

      if (paymentMethod) {
        providers =
          available.find((c) => c.code === paymentMethod)?.providers ?? [];
      }
    }
    return { available, unavailable, providers };
  }, [prePurchase, paymentMethod, hasPermission, role.name]);

  useEffect(() => {
    if (!prePurchase) return;
    if (!available.some((p) => p.code === paymentMethod)) {
      setValue("paymentMethod", null);
    }
    if (!providers.find((p) => p.code === paymentProvider)) {
      setValue(
        "paymentProvider",
        providers.length === 1 ? providers[0].code : null
      );
    }
  }, [prePurchase, paymentMethod, paymentProvider, available, providers, setValue]);

  if (!prePurchase) {
    return (
      <Typography variant="body2" color="text.secondary">
        Definí cliente, dirección y productos para ver los métodos de pago.
      </Typography>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          display: "grid",
          gap: 1,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr",
          },
        }}
      >
        {available.map((method) => (
          <PaymentTile
            key={method.code}
            method={method}
            selected={paymentMethod === method.code}
            onSelect={() =>
              setValue("paymentMethod", method.code, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />
        ))}
        {unavailable.map((method) => (
          <PaymentTile
            key={method.code}
            method={method}
            disabled
            disabledReason={
              method.errors?.[0]
                ? PAYMENT_ERRORS[method.errors[0]] ?? method.errors[0]
                : undefined
            }
          />
        ))}
      </Box>

      {paymentMethod && providers.length > 0 && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              fontWeight: 700,
              display: "block",
              mb: 1,
            }}
          >
            Proveedor de pago
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {providers.map((p) => (
              <ToggleButton
                key={p.code}
                value={p.code}
                selected={paymentProvider === p.code}
                disabled={providers.length === 1}
                onChange={() =>
                  setValue("paymentProvider", p.code, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                size="small"
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2,
                  fontWeight: 600,
                }}
              >
                {providerLabel(p.code)}
              </ToggleButton>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

const PaymentTile: FC<{
  method: TPaymentMethod;
  selected?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onSelect?: () => void;
}> = ({ method, selected, disabled, disabledReason, onSelect }) => {
  const theme = useTheme();
  const icon = ICON_BY_METHOD[method.code] ?? <PaymentsOutlined />;

  const inner = (
    <Box
      onClick={disabled ? undefined : onSelect}
      role={disabled ? undefined : "button"}
      tabIndex={disabled ? undefined : 0}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onSelect?.();
        }
      }}
      sx={{
        position: "relative",
        p: 1.5,
        borderRadius: 2,
        border: 1,
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected
          ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.06)
          : "background.paper",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        transition: "border-color .2s, background-color .2s, transform .15s",
        outline: "none",
        "&:hover": disabled
          ? {}
          : {
              borderColor: "primary.main",
              transform: "translateY(-1px)",
            },
        "&:focus-visible": {
          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}`,
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: selected ? "primary.main" : "action.hover",
            color: selected ? "primary.contrastText" : "text.secondary",
            flexShrink: 0,
            transition: "background-color .2s, color .2s",
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              lineHeight: 1.25,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {methodLabel(method.code)}
          </Typography>
          {disabled && disabledReason && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ mt: 0.25 }}
            >
              <LockOutlined sx={{ fontSize: 12, color: "text.disabled" }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {disabledReason}
              </Typography>
            </Stack>
          )}
          {!disabled && method.providers?.length > 1 && (
            <Typography variant="caption" color="text.secondary">
              {method.providers.length} proveedores
            </Typography>
          )}
        </Box>
        {selected && (
          <CheckCircle
            sx={{
              color: "primary.main",
              fontSize: 18,
              flexShrink: 0,
            }}
          />
        )}
      </Stack>
    </Box>
  );

  if (disabled && disabledReason) {
    return (
      <Tooltip title={disabledReason} placement="top" arrow>
        <Box sx={{ display: "block" }}>{inner}</Box>
      </Tooltip>
    );
  }
  return inner;
};
